import { BatteryCharging, RefreshCw, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n";
import { Reveal, Chapter } from "./shared";
import { TcoCalculator } from "./TcoCalculator";

const BATTERY_IMG = "/assets/catl-battery.jpg";

export const Battery = () => {
  const { t } = useLanguage();
  const b = t.battery;
  return (
    <section id="battery" className="scroll-mt-24 bg-paper" data-testid="battery-section">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        <Chapter num="05" label={b.chapter.split("/ ")[1]} />
        <div className="mt-12 grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 data-testid="battery-heading" className="font-display text-3xl font-extrabold leading-[1.08] tracking-tight text-neutral-950 sm:text-4xl lg:text-5xl">
                {b.heading}
              </h2>
              <p className="mt-6 text-base leading-relaxed text-neutral-500 sm:text-lg">{b.body}</p>
            </Reveal>
            <div className="mt-10 grid grid-cols-3 gap-5">
              {b.stats.map(([value, label], i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div data-testid={`battery-stat-${i}`} className="border-t-2 border-brand-600 pt-3">
                    <div className="font-mono text-2xl font-bold tracking-tighter text-neutral-950 sm:text-3xl">{value}</div>
                    <div className="mt-1 text-xs leading-snug text-neutral-500">{label}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="relative overflow-hidden rounded-[1.75rem] shadow-[0_36px_80px_-44px_rgba(44,56,28,0.5)]" data-testid="battery-image">
                <img src={BATTERY_IMG} alt="Battery cell technology" loading="lazy" className="aspect-[16/10] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-900/35 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 rounded-2xl border border-white/50 bg-white/85 px-4 py-3 backdrop-blur-md">
                  <div className="font-mono text-[10px] tracking-[0.2em] text-brand-700">BATMAT SWAP</div>
                  <div className="font-display text-2xl font-black text-neutral-900">{b.stepTime}</div>
                </div>
              </div>
            </Reveal>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <Reveal delay={0.15}>
                <div data-testid="battery-hadron-card" className="group h-full rounded-[1.5rem] border border-neutral-200 bg-white p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_60px_-30px_rgba(44,56,28,0.35)]">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-100 text-brand-700 transition-colors duration-500 group-hover:bg-brand-600 group-hover:text-white">
                    <BatteryCharging size={20} />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold text-neutral-950">{b.hadronTitle}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-500">{b.hadronDesc}</p>
                  <a
                    href="https://hadronenergy.pl"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="battery-hadron-link"
                    className="mt-4 inline-flex items-center gap-1.5 font-mono text-xs font-medium text-brand-700 underline decoration-brand-200 underline-offset-4 transition-colors hover:text-brand-500"
                  >
                    {b.hadronLink} <ArrowUpRight size={13} />
                  </a>
                </div>
              </Reveal>
              <Reveal delay={0.22}>
                <div data-testid="battery-batmat-card" className="group h-full rounded-[1.5rem] border border-neutral-200 bg-white p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_60px_-30px_rgba(44,56,28,0.35)]">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-100 text-brand-700 transition-colors duration-500 group-hover:bg-brand-600 group-hover:text-white">
                    <RefreshCw size={20} />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold text-neutral-950">{b.batmatTitle}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-500">{b.batmatDesc}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div data-testid="battery-swap-steps" className="mt-14 flex flex-wrap items-center justify-between gap-x-8 gap-y-5 rounded-[1.5rem] bg-brand-900 px-8 py-7">
            {b.steps.map((step, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`battery-step-${i}`}>
                <span className="font-mono text-xs tracking-[0.2em] text-brand-400">{`0${i + 1}`}</span>
                <span className="font-display text-base font-bold text-white sm:text-lg">{step}</span>
                {i < b.steps.length - 1 && <span className="ml-4 hidden h-px w-12 bg-white/20 md:block" />}
              </div>
            ))}
            <span className="rounded-full bg-brand-600 px-5 py-2 font-mono text-sm font-bold text-white" data-testid="battery-step-time">
              {b.stepTime}
            </span>
          </div>
        </Reveal>

        <TcoCalculator />
      </div>
    </section>
  );
};
